import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { from, Observable, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { ContextService } from '../services/context.service';

function getUserIdFromInstance(instance: any): string {
  try {
    const fromProfile = instance?.profile?.id;
    if (fromProfile) return fromProfile;

    const token: string | undefined = instance?.token;
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.sub ?? '';
  } catch {
    return '';
  }
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const keycloak = inject(KeycloakService);
  const router = inject(Router);
  const context = inject(ContextService);

  const keycloakUrl = environment.keycloak.url;
  const publicPaths = ['/assets', '/login', 'bootstrap-italia/i18n'];

  const isPublic =
    publicPaths.some((path) => req.url.includes(path)) ||
    req.url.startsWith(keycloakUrl);

  if (isPublic) {
    return next(req);
  }

  const keycloakInstance = keycloak.getKeycloakInstance();
  const isAuthenticated = !!keycloakInstance && keycloakInstance.authenticated;

  if (!isAuthenticated) {
    console.warn(
      '[AuthInterceptor] Utente non autenticato. Redirect alla login...'
    );
    router.navigate(['/login']);
    return throwError(() => new Error('Utente non autenticato'));
  }

  return from(keycloak.updateToken(30)).pipe(
    mergeMap(() => {
      const instance = keycloak.getKeycloakInstance();
      const token = instance?.token;

      if (!token) {
        console.warn('[AuthInterceptor] Token assente. Redirect alla login...');
        router.navigate(['/login']);
        return throwError(() => new Error('Token assente'));
      }

      const ctx = context.getHeaderValues();
      const userId = getUserIdFromInstance(instance);

      const cloned = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          'Context-actor-code': ctx ? ctx.actorCode : '',
          'Context-actor-reference-code': ctx ? ctx.actorReferenceCode : '',
          'Context-user-id': userId
        },
      });

      return next(cloned);
    }),
    catchError((err) => {
      console.error('[AuthInterceptor] Errore aggiornamento token:', err);
      if (err?.status === 401 || err?.status === 403) {
        console.warn(
          '[AuthInterceptor] Errore di autenticazione, redirect alla login...'
        );
        router.navigate(['/login']);
      } else {
        console.warn('[AuthInterceptor] Errore generico, nessun redirect.');
      }

      return throwError(() => err);
    })
  );
};
